import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import api from "../api/client";
import { toast } from "sonner";
import { CreditCard, Lock } from "lucide-react";

export default function Checkout() {
    const { items, subtotal } = useCart();
    const { user } = useAuth();
    const nav = useNavigate();
    const location = useLocation();
    const couponCode = location.state?.couponCode || "";
    const [submitting, setSubmitting] = useState(false);

    const [form, setForm] = useState({
        full_name: user?.name || "",
        phone: "",
        address_line: "",
        city: "",
        state: "",
        pincode: "",
        country: "India",
    });

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const shipping = subtotal >= 499 ? 0 : 49;
    const tax = Math.round(subtotal * 0.05 * 100) / 100;
    const total = subtotal + tax + shipping;

    const submit = async (e) => {
        e.preventDefault();
        if (!items.length) { toast.error("Cart is empty"); return; }
        setSubmitting(true);
        try {
            const origin = window.location.origin;
            const { data } = await api.post("/checkout/session", {
                address: form,
                coupon_code: couponCode || null,
                origin_url: origin,
            });
            if (data.url) {
                window.location.href = data.url;
            } else {
                toast.error("Failed to get checkout URL");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Checkout failed");
            setSubmitting(false);
        }
    };

    if (!items.length) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-16 text-center">
                <p className="text-brand-moss">Your cart is empty.</p>
                <button onClick={() => nav("/products")} className="mt-4 bg-brand-orange text-white font-bold px-6 py-3 rounded-xl">
                    Browse products
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 animate-fade-slide">
            <h1 className="font-heading font-black text-4xl md:text-6xl tracking-tighter mb-10">Checkout</h1>
            <form onSubmit={submit} className="grid lg:grid-cols-[1fr,380px] gap-10">
                <div className="space-y-8">
                    <section className="bg-white border border-brand-line rounded-2xl p-6 md:p-8">
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-orange mb-2">Step 1</div>
                        <h2 className="font-heading font-bold text-2xl mb-6">Contact & Shipping</h2>
                        <div className="grid sm:grid-cols-2 gap-4">
                            {[
                                ["full_name", "Full name", "text", true],
                                ["phone", "Phone number", "tel", true],
                                ["address_line", "Street address", "text", true, "sm:col-span-2"],
                                ["city", "City", "text", true],
                                ["state", "State", "text", true],
                                ["pincode", "Pincode", "text", true],
                                ["country", "Country", "text", true],
                            ].map(([name, label, type, required, cls]) => (
                                <div key={name} className={cls || ""}>
                                    <label className="text-xs font-bold text-brand-moss mb-2 uppercase tracking-wider block">
                                        {label}
                                    </label>
                                    <input
                                        type={type}
                                        required={required}
                                        value={form[name]}
                                        onChange={(e) => setField(name, e.target.value)}
                                        data-testid={`checkout-${name}`}
                                        className="w-full bg-brand-cream border-none rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand-orange"
                                    />
                                </div>
                            ))}
                        </div>
                    </section>

                    <section className="bg-white border border-brand-line rounded-2xl p-6 md:p-8">
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-orange mb-2">Step 2</div>
                        <h2 className="font-heading font-bold text-2xl mb-6">Payment</h2>
                        <div className="bg-brand-cream border border-brand-line rounded-xl p-5 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-brand-ink text-white flex items-center justify-center">
                                <CreditCard size={22} />
                            </div>
                            <div>
                                <p className="font-bold">Secure card payment via Stripe</p>
                                <p className="text-xs text-brand-moss flex items-center gap-1 mt-0.5">
                                    <Lock size={12} /> 256-bit encrypted · Test mode
                                </p>
                            </div>
                        </div>
                    </section>
                </div>

                <aside className="lg:sticky lg:top-28 self-start bg-white border border-brand-line rounded-2xl p-6">
                    <h3 className="font-heading font-bold text-xl mb-4">Order summary</h3>
                    <div className="space-y-3 max-h-64 overflow-auto mb-4">
                        {items.map((it) => (
                            <div key={it.product_id} className="flex gap-3 text-sm">
                                <div className="w-14 h-14 bg-brand-sand rounded-lg overflow-hidden shrink-0">
                                    <img src={it.product.images?.[0]} alt={it.product.name} className="w-full h-full object-cover" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold truncate">{it.product.name}</p>
                                    <p className="text-xs text-brand-moss">{it.product.pack_size} × {it.quantity}</p>
                                </div>
                                <p className="font-bold">₹{(it.product.price * it.quantity).toFixed(0)}</p>
                            </div>
                        ))}
                    </div>
                    <div className="border-t border-brand-line pt-4 space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span>Subtotal</span>
                            <span className="font-bold">₹{subtotal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span>Tax</span>
                            <span className="font-bold">₹{tax.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span>Shipping</span>
                            <span className="font-bold">{shipping === 0 ? "FREE" : `₹${shipping}`}</span>
                        </div>
                        <div className="flex justify-between text-lg pt-2 border-t border-brand-line">
                            <span className="font-heading font-bold">Total</span>
                            <span className="font-heading font-black">₹{total.toFixed(2)}</span>
                        </div>
                    </div>
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="place-order-btn"
                        className="w-full mt-5 bg-brand-orange text-white font-bold py-4 rounded-xl btn-lift disabled:opacity-50 shadow-[0_4px_14px_0_rgba(255,107,53,0.39)]"
                    >
                        {submitting ? "Redirecting..." : `Pay ₹${total.toFixed(2)}`}
                    </button>
                    <p className="text-xs text-brand-moss text-center mt-3">
                        You'll be redirected to Stripe's secure checkout.
                    </p>
                </aside>
            </form>
        </div>
    );
}
